/**
 * Ward Smelling Salts – "View cart" button
 *
 * After a product is successfully added to the cart from a product page,
 * the "Add to cart" buttons change to "View cart" and link to the cart.
 * This covers the main button, the sticky add-to-cart bar, and the
 * "Quick add to cart" button beside the star rating (blocks/ward-quick-add-to-cart.liquid).
 *
 * The buttons go back to "Add to cart" when:
 *   - the page is reloaded or revisited (nothing is stored between visits)
 *   - the shopper picks a different variant
 *   - the shopper changes the quantity
 *
 * Quick-add buttons on product cards and in quick-add popups are left alone.
 */
(() => {
  if (window.__wardViewCartButton) return;
  window.__wardViewCartButton = true;

  const VIEW_CART_TEXT = 'View cart';
  const ADDING_TEXT = 'Adding...';
  const STATE_ATTR = 'data-view-cart';
  const QUICK_SELECTOR = '.ward-quick-atc';
  const MAIN_SELECTOR = 'product-form-component add-to-cart-component [ref="addToCartButton"]';

  const cartUrl = () => window.Theme?.routes?.cart_url || '/cart';

  /** Only product-page forms, not product cards or quick-add popups. */
  const isEligibleForm = (form) =>
    form instanceof HTMLElement &&
    form.matches('product-form-component') &&
    !form.closest('product-card, dialog, .quick-add-modal');

  const sectionOf = (el) => el.closest('.shopify-section') || document;

  /** Main button, sticky bar button, and the quick add button beside the reviews. */
  const getButtons = (form) => {
    const section = sectionOf(form);
    return [
      ...form.querySelectorAll('add-to-cart-component [ref="addToCartButton"]'),
      ...section.querySelectorAll('.sticky-add-to-cart__button'),
      ...section.querySelectorAll(QUICK_SELECTOR),
    ];
  };

  /** The main product form button in the same section as an element. */
  const getMainButton = (el) => {
    const section = sectionOf(el);
    return [...section.querySelectorAll(MAIN_SELECTOR)].find((btn) =>
      isEligibleForm(btn.closest('product-form-component'))
    );
  };

  /** The span holding the button label (main button nests one level deeper than the others). */
  const getLabel = (button) => {
    const outer = button.querySelector('.add-to-cart-text__content > span');
    return outer?.querySelector(':scope > span') || outer;
  };

  const getIcon = (button) => button.querySelector('.add-to-cart-icon');

  const rememberLabel = (button, label) => {
    if (!button.dataset.originalLabel) {
      button.dataset.originalLabel = label.textContent.trim();
    }
  };

  const showViewCart = (button) => {
    const label = getLabel(button);
    if (!label || button.disabled) return;

    rememberLabel(button, label);
    label.textContent = VIEW_CART_TEXT;
    const icon = getIcon(button);
    if (icon) icon.style.display = 'none';
    button.removeAttribute('data-adding');
    button.setAttribute(STATE_ATTR, 'true');
  };

  const showAddToCart = (button) => {
    const label = getLabel(button);
    if (label && button.dataset.originalLabel) {
      label.textContent = button.dataset.originalLabel;
    }
    const icon = getIcon(button);
    if (icon) icon.style.display = '';
    button.removeAttribute('data-adding');
    button.removeAttribute(STATE_ATTR);
  };

  const resetForm = (form) =>
    getButtons(form).forEach((button) => {
      if (button.getAttribute(STATE_ATTR) === 'true') showAddToCart(button);
    });

  /** Put any "Adding..." quick buttons back to "Add to cart" (used when an add fails). */
  const clearAdding = (scope = document) =>
    scope.querySelectorAll(`${QUICK_SELECTOR}[data-adding="true"]`).forEach(showAddToCart);

  /** Wait for the theme's "added" checkmark animation to finish before swapping the label. */
  const afterAddedAnimation = (button, callback) => {
    const started = Date.now();
    const check = () => {
      if (!button.hasAttribute('data-added') || Date.now() - started > 900) {
        callback();
      } else {
        setTimeout(check, 100);
      }
    };
    setTimeout(check, 300);
  };

  // Keep the quick button's enabled/sold-out state in step with the main button.
  const syncQuickButtons = (scope = document) => {
    scope.querySelectorAll(QUICK_SELECTOR).forEach((quick) => {
      const main = getMainButton(quick);
      if (!main) return;
      quick.disabled = main.disabled;
      if (quick.getAttribute(STATE_ATTR) !== 'true' && !quick.hasAttribute('data-adding')) {
        const quickLabel = getLabel(quick);
        const mainLabel = getLabel(main);
        if (quickLabel && mainLabel) quickLabel.textContent = mainLabel.textContent.trim();
        delete quick.dataset.originalLabel;
      }
    });
  };

  // 1. Successful add to cart → switch to "View cart". Failed add → undo "Adding...".
  document.addEventListener('cart:update', (event) => {
    const form = event.target;
    const data = event.detail?.data;
    if (!isEligibleForm(form)) return;
    if (data?.source !== 'product-form-component') return;

    if (data?.didError) {
      clearAdding(sectionOf(form));
      return;
    }

    getButtons(form).forEach((button) => afterAddedAnimation(button, () => showViewCart(button)));
  });

  document.addEventListener('cart:error', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    clearAdding(target ? sectionOf(target) : document);
  });

  // 2. Clicking "View cart" goes to the cart instead of adding again.
  //    Capture phase so this runs before the theme's own add-to-cart handlers.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.(`[${STATE_ATTR}="true"]`);
      if (!button || !button.matches(`[ref="addToCartButton"], ${QUICK_SELECTOR}`)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.href = cartUrl();
    },
    true
  );

  // 3. Quick add button → press the main "Add to cart" button, so the selected
  //    variant, quantity and the theme's normal add-to-cart handling are all used.
  document.addEventListener('click', (event) => {
    const quick = event.target.closest?.(QUICK_SELECTOR);
    if (!quick || quick.disabled || quick.hasAttribute('data-adding')) return;

    const main = getMainButton(quick);
    if (!main || main.disabled) return;

    const label = getLabel(quick);
    if (label) {
      rememberLabel(quick, label);
      label.textContent = ADDING_TEXT;
    }
    quick.setAttribute('data-adding', 'true');

    // Safety net: never leave the button stuck on "Adding...".
    setTimeout(() => {
      if (quick.hasAttribute('data-adding')) showAddToCart(quick);
    }, 8000);

    main.click();
  });

  // 4. Variant or quantity change → back to "Add to cart".
  const resetFromEvent = (event) => {
    const scope = event.target?.closest?.('.shopify-section') || document;
    scope.querySelectorAll('product-form-component').forEach((form) => {
      if (isEligibleForm(form)) resetForm(form);
    });
  };

  document.addEventListener('variant:update', (event) => {
    resetFromEvent(event);
    // The theme updates the main button after this event; copy its state on the next tick.
    setTimeout(() => syncQuickButtons(sectionOf(event.target)), 0);
  });
  document.addEventListener('quantity-selector:update', (event) => {
    // Ignore quantity changes inside the cart drawer.
    if (event.target?.closest?.('product-form-component')) resetFromEvent(event);
  });
  document.addEventListener('input', (event) => {
    if (event.target?.matches?.('product-form-component input[name="quantity"]')) resetFromEvent(event);
  });

  // 5. Coming back with the browser's Back button can restore the old page state → reset it.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(`[${STATE_ATTR}="true"], ${QUICK_SELECTOR}[data-adding="true"]`).forEach(showAddToCart);
  });
})();
